import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Grid2 as Grid,
    Skeleton,
    styled,
    Typography,
} from '@mui/material';

import { ApiStatus } from '../../utils/enums';
import { formatCurrency, formatNumber } from '../../utils/formattingUtils';
import { buildAreaBalanceLink } from '../../utils/navigationHelpers';
import { useFilters } from '../Filters/useFilters';
import BalanceLabel from '../Labels/BalanceLabel';

const classesPrefix = 'areaBalance';

const classes = {
    clickableWidget: `${classesPrefix}-clickableWidget`,
    balanceContainer: `${classesPrefix}-balanceContainer`,
    totalOrders: `${classesPrefix}-totalOrders`,
    label: `${classesPrefix}-label`,
    areaNameLabel: `${classesPrefix}-areaNameLabel`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`&.${classes.clickableWidget}`]: {
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
                cursor: 'pointer',
            },
        },
        [`& .${classes.balanceContainer}`]: {
            display: 'flex',
            justifyContent: 'flex-end',
        },
        [`& .${classes.totalOrders}`]: {
            display: 'flex',
            paddingRight: '8px',
        },
        [`& .${classes.label}`]: {
            display: 'flex',
            flexGrow: 1,
        },
        [`& .${classes.areaNameLabel}`]: {
            fontWeight: 600,
            paddingLeft: '4px',
            display: 'flex',
            flexGrow: 1,
        },
    };
});

const AreaBalance = ({
    title,
    apiStatus,
    inboundOrders,
    outboundOrders,
    intraAreaOrders,
    isDailyBalance,
    areaYield,
    averageBalance,
    enableNavigationClick = false,
}: {
    title: string;
    apiStatus: ApiStatus;
    inboundOrders: number;
    outboundOrders: number;
    intraAreaOrders: number;
    isDailyBalance: boolean;
    areaYield?: number | null;
    averageBalance?: number | null;
    enableNavigationClick?: boolean;
}): React.JSX.Element => {
    const navigate = useNavigate();
    const { name } = useParams<{ name: string; }>();
    const filters = useFilters();

    const loading = apiStatus === ApiStatus.Idle || apiStatus === ApiStatus.Loading;
    const balance = averageBalance !== null && averageBalance !== undefined ? averageBalance : inboundOrders - outboundOrders;

    return (
        <StyledGrid
            className={enableNavigationClick ? classes.clickableWidget : undefined}
            container
            direction='column'
            justifyContent='flex-end'
            onClick={(): void => {
                if (enableNavigationClick && name) {
                    navigate(buildAreaBalanceLink(name, filters));
                }
            }}
        >
            <Grid size='auto'>
                <Typography variant='h4'>
                    {title}
                </Typography>
            </Grid>
            <Grid size='grow' className={classes.balanceContainer}>
                <BalanceLabel
                    apiStatus={apiStatus}
                    balanceType='area'
                    isDailyBalance={isDailyBalance}
                    balanceAmount={balance}
                />
            </Grid>
            <Grid>
                <Grid container direction='column' spacing={1}>
                    {
                        areaYield !== undefined &&
                        <Grid size={12}>
                            <Typography variant='body2' gutterBottom className={classes.totalOrders}>
                                <span className={classes.label}>Avg. Area Yield</span>
                                <span>{loading ? <Skeleton /> : formatCurrency(areaYield, 0, 0)}</span>
                            </Typography>
                        </Grid>
                    }

                    <Grid size={12}>
                        <Typography variant='body2' gutterBottom className={classes.totalOrders}>
                            <span>Total orders into</span>
                            <span className={classes.areaNameLabel}>{name}</span>
                            <span>{loading ? <Skeleton /> : formatNumber(inboundOrders, 0, 0)}</span>
                        </Typography>
                    </Grid>

                    <Grid size={12}>
                        <Typography variant='body2' gutterBottom className={classes.totalOrders}>
                            <span>Total orders out of</span>
                            <span className={classes.areaNameLabel}>{name}</span>
                            <span>{loading ? <Skeleton /> : formatNumber(outboundOrders, 0, 0)}</span>
                        </Typography>
                    </Grid>

                    <Grid size={12}>
                        <Typography variant='body2' gutterBottom className={classes.totalOrders}>
                            <span>Total intra area orders</span>
                            <span className={classes.areaNameLabel}>{name}</span>
                            <span>{loading ? <Skeleton /> : formatNumber(intraAreaOrders, 0, 0)}</span>
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
        </StyledGrid>
    );
};

export default AreaBalance;
